import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Calendar,
    Users,
    Palmtree,
    CalendarDays,
    Sparkles,
    UserCog,
    Ticket,
    ChevronDown,
    Bell,
    Send,
    History,
    Tag,
    CalendarPlus,
    Clock,
    Bus,
    Award,
    DollarSign,
    Shield,
    FileText,
    RefreshCw,
    Menu,
    X,
    PanelLeftClose,
    PanelLeftOpen,
    LogOut,
    AlertTriangle,
    Settings,
    BarChart3,
    BookOpen,
    TrendingUp
} from 'lucide-react';

// Componente de sección colapsable
const CollapsibleSection = ({ title, icon, items, defaultOpen = true, isVisible = true, onLinkClick, isCollapsed }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const location = useLocation();

    // Sincronizar isOpen con isCollapsed: si se colapsa el sidebar, cerramos las secciones para evitar scroll innecesario
    useEffect(() => {
        if (isCollapsed) setIsOpen(false);
    }, [isCollapsed]);

    // Verificar si algún item de esta sección está activo
    const safeItems = Array.isArray(items) ? items : [];
    const hasActiveItem = safeItems.some(item =>
        item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
    );

    if (!isVisible || safeItems.length === 0) return null;

    return (
        <div className="relative group/section">
            <button
                onClick={() => !isCollapsed && setIsOpen(!isOpen)}
                className={`w-full flex items-center px-3 py-2.5 rounded-xl transition-all duration-300 hover:bg-white/5 group ${hasActiveItem ? 'text-cyan-400 bg-cyan-400/5' : 'text-gray-400'} ${isCollapsed ? 'justify-center' : 'justify-between'}`}
                title={isCollapsed ? title : ''}
            >
                <div className="flex items-center gap-3">
                    <span className={`${isCollapsed ? 'scale-110' : ''} transition-transform duration-300`}>{icon}</span>
                    {!isCollapsed && (
                        <span className="text-xs uppercase tracking-wider font-semibold whitespace-nowrap">
                            {title}
                        </span>
                    )}
                </div>
                {!isCollapsed && (
                    <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${isOpen ? '' : '-rotate-90'}`}
                    />
                )}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${isOpen && !isCollapsed ? 'max-h-96 opacity-100 mt-2 ml-4 border-l border-white/5 pl-2' : 'max-h-0 opacity-0'}`}>
                {safeItems.map(item => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.end}
                        onClick={onLinkClick}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:text-white mb-1 ${isActive ? 'text-cyan-400 bg-cyan-400/10 font-medium' : 'text-gray-500'}`}
                    >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </div>

            {/* Tooltip simple para modo colapsado */}
            {isCollapsed && (
                <div className="fixed left-20 ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover/section:opacity-100 pointer-events-none transition-opacity duration-200 z-[100] whitespace-nowrap border border-white/10 shadow-xl font-medium">
                    {title}
                </div>
            )}
        </div>
    );
};

const DashboardLayout = () => {
    const { user, logout, isAdmin, hasPermission } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Check admin status once for the whole render
    const isUserAdmin = typeof isAdmin === 'function' ? isAdmin() : false;

    // Helper to filter items by permission
    const filterByPermission = (items) => {
        if (isUserAdmin) return items; // Admins see everything
        return items.filter(item => {
            if (!item.permission) return true; // No permission required = always show
            return hasPermission(item.permission);
        });
    };

    // Cerrar sidebar al navegar (para móvil)
    const handleNavClick = () => {
        if (window.innerWidth < 1024) {
            setSidebarOpen(false);
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Definir secciones
    const sections = [
        {
            title: "Usuarios",
            icon: <UserCog className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/usuarios', icon: <UserCog className="w-4 h-4" />, label: 'Usuarios', permission: 'usuarios.ver' },
                { path: '/admin/invitaciones', icon: <Ticket className="w-4 h-4 text-cyan-400" />, label: 'Invitaciones', permission: 'invitaciones.ver' },
                { path: '/admin/roles', icon: <Shield className="w-4 h-4 text-purple-400" />, label: 'Roles y Permisos', permission: 'configuracion.roles.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Conductores",
            icon: <Users className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/conductores', icon: <Users className="w-4 h-4" />, label: 'Conductores', permission: 'conductores.ver' },
                { path: '/admin/asuntos-propios', icon: <FileText className="w-4 h-4 text-amber-400" />, label: 'Asuntos Propios', permission: 'ap.ver' },
                { path: '/admin/horas-sindicales', icon: <Clock className="w-4 h-4 text-cyan-400" />, label: 'Horas Sindicales', permission: 'configuracion.ver' },
                { path: '/admin/cambios-historial', icon: <RefreshCw className="w-4 h-4 text-blue-400" />, label: 'Historial Cambios', permission: 'intercambios.ver' },
            ]),
            isVisible: true
        },
        {
            title: "Accidentes",
            icon: <AlertTriangle className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/accidentes', icon: <FileText className="w-4 h-4 text-red-400" />, label: 'Listado Partes', permission: 'accidentes.ver' },
            ]),
            isVisible: true
        },
        {
            title: "Calendario",
            icon: <Calendar className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin', icon: <Calendar className="w-4 h-4" />, label: 'Calendario', end: true, permission: 'calendario.ver' },
                { path: '/admin/festivos', icon: <Sparkles className="w-4 h-4 text-yellow-400" />, label: 'Festivos', permission: 'configuracion.festivos.ver' },
                { path: '/admin/grupos-vacaciones', icon: <Palmtree className="w-4 h-4" />, label: 'Grupos Vacaciones', permission: 'vacaciones.ver' },
                { path: '/admin/grupos-libranzas', icon: <CalendarDays className="w-4 h-4" />, label: 'Grupos Libranzas', permission: 'libranzas.ver' },
                { path: '/admin/anexo-libranzas', icon: <CalendarPlus className="w-4 h-4 text-purple-400" />, label: 'Anexo Libranzas', permission: 'anexo.ver' },
                { path: '/admin/importar-nombramiento', icon: <FileText className="w-4 h-4 text-pink-400" />, label: 'Importar PDF', permission: 'configuracion.turnos.ver' },
                { path: '/admin/gmail-config', icon: <Send className="w-4 h-4 text-red-400" />, label: 'Email Automático', permission: 'configuracion.turnos.ver' },
                { path: '/admin/turnos', icon: <Clock className="w-4 h-4 text-cyan-400" />, label: 'Turnos', permission: 'configuracion.turnos.ver' },
                { path: '/admin/motivos', icon: <Tag className="w-4 h-4 text-red-400" />, label: 'Motivos', permission: 'configuracion.motivos.ver' },
            ]),
            isVisible: true
        },
        {
            title: "Avisos",
            icon: <Bell className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/avisos', icon: <Send className="w-4 h-4" />, label: 'Enviar Aviso', end: true, permission: 'avisos.ver' },
                { path: '/admin/avisos/historial', icon: <History className="w-4 h-4" />, label: 'Historial', permission: 'avisos.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Líneas",
            icon: <Bus className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/lineas', icon: <Bus className="w-4 h-4" />, label: 'Líneas y Servidores', permission: 'nomina.lineas.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Nómina",
            icon: <DollarSign className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/primas', icon: <Award className="w-4 h-4 text-green-400" />, label: 'Primas', permission: 'nomina.primas.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Estadísticas",
            icon: <BarChart3 className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/estadisticas', icon: <BarChart3 className="w-4 h-4 text-blue-400" />, label: 'Estadísticas Operativas', permission: 'configuracion.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Auditoría",
            icon: <Shield className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/auditoria', icon: <Shield className="w-4 h-4" />, label: 'Auditoría Técnica', permission: 'auditoria.ver' },
                { path: '/admin/actividad', icon: <Users className="w-4 h-4 text-cyan-400" />, label: 'Actividad de Usuarios', permission: 'auditoria.ver' },
                { path: '/admin/metricas-impacto', icon: <TrendingUp className="w-4 h-4 text-emerald-400" />, label: 'Adopción e Impacto', permission: 'auditoria.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "Configuración",
            icon: <Settings className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/ajustes', icon: <Settings className="w-4 h-4 text-gray-400" />, label: 'Ajustes Generales', permission: 'configuracion.ver' },
            ]),
            isVisible: isUserAdmin
        },
        {
            title: "OPE",
            icon: <BookOpen className="w-5 h-5" />,
            items: filterByPermission([
                { path: '/admin/ope', icon: <BookOpen className="w-4 h-4 text-indigo-400" />, label: 'Gestión OPE', permission: 'configuracion.ver' },
            ]),
            isVisible: isUserAdmin
        },
    ];

    return (
        <div className="flex min-h-screen bg-[#0a0c10] text-gray-100">
            {/* Mobile header with hamburger */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0f1117]/95 border-b border-white/5 px-4 py-3 flex items-center justify-between backdrop-blur-md">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">🚌</span>
                    <h2 className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent">
                        TUVISA
                    </h2>
                </div>
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                bg-[#0f1117]/90 backdrop-blur-xl border-r border-white/5 flex flex-col fixed h-screen z-50
                transition-all duration-300 ease-in-out
                ${isCollapsed ? 'w-20' : 'w-72'}
                lg:translate-x-0
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                {/* Logo & Toggle - Fixed at top */}
                <div className={`flex items-center ${isCollapsed ? 'justify-center p-6' : 'justify-between p-6'} border-b border-white/5 flex-shrink-0`}>
                    {!isCollapsed && (
                        <div className="flex items-center gap-3 overflow-hidden">
                            <span className="text-3xl shrink-0">🚌</span>
                            <div className="flex flex-col">
                                <h2 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent whitespace-nowrap">
                                    TUVISA
                                </h2>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">Admin Panel</span>
                            </div>
                        </div>
                    )}
                    {isCollapsed && <span className="text-3xl">🚌</span>}

                    <button
                        onClick={toggleCollapse}
                        className={`hidden lg:flex p-2 rounded-xl hover:bg-white/5 transition-colors text-gray-500 hover:text-cyan-400 ${isCollapsed ? 'mt-2' : ''}`}
                    >
                        {isCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                    </button>
                </div>

                {/* Navigation - Scrollable */}
                <nav className={`flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
                    {sections.map((section, idx) => (
                        <CollapsibleSection
                            key={idx}
                            {...section}
                            isCollapsed={isCollapsed}
                            onLinkClick={handleNavClick}
                            defaultOpen={false}
                        />
                    ))}
                </nav>

                {/* User info & logout - Fixed at bottom */}
                <div className={`p-4 border-t border-white/5 flex-shrink-0 bg-[#0f1117]/50`}>
                    <div className={`flex items-center gap-3 p-3 bg-white/5 rounded-2xl mb-3 transition-all duration-300 ${isCollapsed ? 'justify-center p-2' : ''}`}>
                        <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-cyan-400 to-green-400 flex items-center justify-center font-bold text-gray-900 shadow-lg shadow-cyan-500/20">
                            {(user?.nombre || user?.email || 'U').charAt(0).toUpperCase()}
                        </div>
                        {!isCollapsed && (
                            <div className="overflow-hidden">
                                <p className="font-semibold text-sm whitespace-nowrap overflow-hidden text-ellipsis">{user?.nombre || user?.email || 'Usuario'}</p>
                                <p className="text-[10px] text-cyan-400/70 uppercase tracking-wider font-bold">{user?.role || 'viewer'}</p>
                            </div>
                        )}
                    </div>
                    {isCollapsed ? (
                        <button
                            onClick={logout}
                            className="w-full flex justify-center p-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all duration-200"
                            title="Cerrar Sesión"
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    ) : (
                        <button onClick={logout} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all duration-200 font-medium text-sm border border-white/5">
                            <LogOut className="w-4 h-4" />
                            Cerrar Sesión
                        </button>
                    )}
                </div>
            </aside>

            {/* Main content */}
            <main className={`
                flex-1 p-4 lg:p-8 pt-20 lg:pt-8 transition-all duration-300
                ${isCollapsed ? 'lg:ml-20' : 'lg:ml-72'}
            `}>
                <div className="max-w-[1600px] mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;
